import { useEffect, useRef, type ReactNode } from "react";

/**
 * Panneau latéral qui s'ouvre par-dessus l'écran sans le quitter (EF-0409).
 *
 * L'écran d'origine reste en place derrière lui, filtres et défilement
 * compris : refermer le tiroir rend l'utilisateur exactement où il était.
 * `Échap` le ferme, comme la grille de saisie, et le focus y entre à
 * l'ouverture pour que le clavier suffise.
 */
export function Tiroir({
  titre,
  surTitre,
  surFermeture,
  retour,
  children,
}: {
  titre: ReactNode;
  surTitre?: ReactNode;
  surFermeture: () => void;
  /** Revenir au niveau précédent de la remontée, sans fermer. */
  retour?: { libelle: string; action: () => void };
  children: ReactNode;
}) {
  const panneau = useRef<HTMLElement>(null);
  // Les rappels changent à chaque rendu ; l'écouteur, lui, est posé une fois
  // et lit toujours les derniers.
  const rappels = useRef({ retour, surFermeture });
  useEffect(() => {
    rappels.current = { retour, surFermeture };
  });

  // Le focus entre dans le tiroir à l'ouverture, et à chaque changement de
  // niveau — pas à chaque rendu, ce qui le volerait à la ligne choisie.
  const niveau = retour?.libelle ?? "";
  useEffect(() => {
    panneau.current?.focus();
  }, [niveau]);

  useEffect(() => {
    const surTouche = (evenement: KeyboardEvent) => {
      if (evenement.key !== "Escape") return;
      evenement.preventDefault();
      const { retour: precedent, surFermeture: fermer } = rappels.current;
      if (precedent != null) precedent.action();
      else fermer();
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, []);

  return (
    <div className="tiroir-fond" onMouseDown={(e) => e.target === e.currentTarget && surFermeture()}>
      <aside className="tiroir" ref={panneau} tabIndex={-1} role="dialog" aria-modal="true">
        <header className="tiroir-entete">
          <div>
            {retour != null && (
              <button type="button" className="tertiaire tiroir-retour" onClick={retour.action}>
                ← {retour.libelle}
              </button>
            )}
            {surTitre != null && <div className="discret-texte">{surTitre}</div>}
            <h2>{titre}</h2>
          </div>
          <button type="button" className="tertiaire" onClick={surFermeture} aria-label="Fermer">
            ✕
          </button>
        </header>
        <div className="tiroir-contenu">{children}</div>
      </aside>
    </div>
  );
}
